import { Navigate, useLocation } from "react-router-dom";
import { useAppSelector } from "../../app/hooks";
import {
  selectAuthenticatedRole,
  selectAuthInitializationStatus,
  selectIsAuthenticated,
} from "../../features/authentication/authenticationSlice";
import { JSX } from "react";
import { ROUTES } from "../../routes";

const RequireAuth = ({
  children,
  roles,
}: {
  children: JSX.Element;
  roles?: string | string[];
}) => {
  // Bypass authentication in development mode (requires both flags to be true)
  const bypassAuth = import.meta.env.VITE_BYPASS_AUTH === 'true' && import.meta.env.VITE_DEBUG_MODE === 'true';

  if (bypassAuth) {
    return children;
  }

  const role = useAppSelector(selectAuthenticatedRole);
  const isAuthenticated = useAppSelector(selectIsAuthenticated);
  const isInitialized = useAppSelector(selectAuthInitializationStatus);
  const location = useLocation();

  // Don't mount protected pages until the auth state is hydrated: child
  // effects run before parent effects, so rendering early lets data fetches
  // fire without the Authorization header — the resulting 401 triggers the
  // global logout interceptor and bounces a deep-linked user to /login.
  if (isInitialized !== "initialized") {
    return null;
  }

  if (!isAuthenticated && location.pathname !== ROUTES.LOGIN) {
    return <Navigate to={ROUTES.LOGIN} replace />;
  }

  const allowedRoles = Array.isArray(roles) ? roles : roles ? [roles] : undefined;
  if (allowedRoles && !allowedRoles.includes(role)) {
    return <Navigate to={ROUTES.HOME} replace />;
  }

  return children;
};

export default RequireAuth;